import { useCallback, useEffect, useState } from "react";
import { Copy, KeyRound, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

const READ_ENDPOINTS = [
  ["GET /api/v1/ping", "Check the key works"],
  ["GET /api/v1/matters", "Every matter with its client, firm, contact persons and amounts"],
  ["GET /api/v1/matters/{matter_id}", "One matter with its full fee-note history"],
  ["GET /api/v1/fee-notes", "All fee notes · filter by status, matter_id, client_id, firm_id"],
  ["GET /api/v1/fee-notes/{invoice_id}", "One fee note with line items, receipts and a PDF link"],
  ["GET /api/v1/payments", "Receipts recorded · filter by invoice_id, date_from, date_to"],
  ["GET /api/v1/clients", "Clients with address, GST number and balances"],
  ["GET /api/v1/law-firms", "Instructing firms with balances"],
  ["GET /api/v1/contacts", "Instructing contact persons"],
  ["GET /api/v1/summary", "Dashboard figures: outstanding, overdue, collected, ageing"],
  ["GET /api/v1/ledger", "Ledger grouped by firm, lawyer or matter"],
  ["GET /api/v1/tds", "TDS register by financial year"],
];

const WRITE_ENDPOINTS = [
  ["POST /api/v1/fee-notes", "Raise a fee note — status 'draft' or 'sent'"],
  ["POST /api/v1/fee-notes/{invoice_id}/payments", "Record a receipt, with TDS if deducted"],
  ["POST /api/v1/matters", "Add a matter"],
  ["POST /api/v1/clients", "Add a client"],
  ["POST /api/v1/contacts", "Add an instructing contact person"],
  ["POST /api/v1/law-firms", "Add a law firm"],
];

export const ApiKeysPanel = () => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [scope, setScope] = useState("read");
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api-keys");
      setKeys(data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load your API keys"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!name.trim()) {
      toast.error("Give the key a name so you know which app holds it");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/api-keys", { name: name.trim(), scope });
      setFresh(data.api_key);
      setName("");
      await load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not issue the key"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (keyId) => {
    if (!window.confirm("Revoke this key? The other app will stop working at once.")) return;
    try {
      await api.delete(`/api-keys/${keyId}`);
      toast.success("Key revoked");
      await load();
    } catch (error) {
      toast.error(errorMessage(error, "Could not revoke the key"));
    }
  };

  const copy = async (value) => {
    await navigator.clipboard.writeText(value);
    toast.success("Copied");
  };

  return (
    <section className="surface p-6" data-testid="settings-api-keys">
      <div className="flex items-start gap-3">
        <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-brand-terracotta" strokeWidth={1.8} />
        <div>
          <h2 className="font-serif text-xl text-slate-900">API access for another app</h2>
          <p className="mt-2 max-w-2xl text-[12px] leading-relaxed text-slate-500">
            Issue a key so another application &mdash; or an AI assistant over MCP &mdash; can work
            with your chambers data. A <strong>read-only</strong> key can never change anything; a{" "}
            <strong>read &amp; write</strong> key can also raise fee notes and record receipts. The
            key is shown once &mdash; copy it into the other app and keep it on that app&rsquo;s
            server, never in a browser.
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              create();
            }
          }}
          placeholder="What holds this key? e.g. Clerk's diary app"
          className="rounded-sm sm:max-w-sm"
          data-testid="api-key-name-input"
        />
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="rounded-sm sm:w-56" data-testid="api-key-scope-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="read">Read only</SelectItem>
            <SelectItem value="write">Read &amp; write</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={create}
          disabled={busy}
          data-testid="create-api-key-button"
          className="inline-flex items-center justify-center gap-2 bg-slate-900 px-5 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Issue key
        </button>
      </div>

      {fresh && (
        <div className="mt-5 border border-amber-300 bg-amber-50 p-4" data-testid="api-key-reveal">
          <p className="text-[12px] font-semibold text-amber-900">
            Copy this now — it will not be shown again.
          </p>
          <div className="mt-3 flex items-start gap-3">
            <code className="num min-w-0 flex-1 break-all text-[12px] text-slate-900">{fresh}</code>
            <button
              type="button"
              onClick={() => copy(fresh)}
              data-testid="copy-api-key-button"
              className="inline-flex shrink-0 items-center gap-1.5 border border-amber-400 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-amber-900 transition-colors duration-150 hover:bg-amber-100"
            >
              <Copy className="h-3 w-3" strokeWidth={2} />
              Copy
            </button>
          </div>
          <button
            type="button"
            onClick={() => setFresh(null)}
            data-testid="dismiss-api-key-button"
            className="mt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-900 underline-offset-4 hover:underline"
          >
            I have saved it
          </button>
        </div>
      )}

      {keys.length > 0 && (
        <ul className="mt-6 divide-y divide-slate-100 border-y border-slate-100" data-testid="api-key-list">
          {keys.map((k) => (
            <li key={k.key_id} className="flex items-center justify-between gap-4 py-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate text-[13px] text-slate-900">{k.name}</p>
                  <span
                    data-testid={`api-key-scope-${k.key_id}`}
                    className={`border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] ${
                      k.scope === "write"
                        ? "border-brand-terracotta bg-[#FBF0EC] text-brand-terracotta"
                        : "border-slate-300 text-slate-500"
                    }`}
                  >
                    {k.scope === "write" ? "Read & write" : "Read only"}
                  </span>
                </div>
                <p className="num truncate text-[11px] text-slate-400">
                  {k.hint} · {k.last_used_at ? `last used ${k.last_used_at.slice(0, 10)}` : "never used"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => revoke(k.key_id)}
                data-testid={`revoke-api-key-${k.key_id}`}
                className="shrink-0 border border-slate-200 p-2 text-slate-400 transition-colors duration-150 hover:border-red-300 hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 border-t border-slate-100 pt-5">
        <p className="label-caps">Reading — any key</p>
        <dl className="mt-3 space-y-2">
          {READ_ENDPOINTS.map(([path, what]) => (
            <div key={path} className="sm:flex sm:gap-4">
              <dt className="num shrink-0 text-[11px] text-slate-800 sm:w-[330px]">{path}</dt>
              <dd className="text-[11px] text-slate-500">{what}</dd>
            </div>
          ))}
        </dl>

        <p className="label-caps mt-6">Writing — read &amp; write keys only</p>
        <dl className="mt-3 space-y-2">
          {WRITE_ENDPOINTS.map(([path, what]) => (
            <div key={path} className="sm:flex sm:gap-4">
              <dt className="num shrink-0 text-[11px] text-slate-800 sm:w-[330px]">{path}</dt>
              <dd className="text-[11px] text-slate-500">{what}</dd>
            </div>
          ))}
        </dl>

        <p className="num mt-5 break-all border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
          curl -H &quot;X-API-Key: ck_live_…&quot; {BASE}/api/v1/fee-notes
        </p>
      </div>

      <div className="mt-6 border-t border-slate-100 pt-5" data-testid="mcp-connector-help">
        <p className="label-caps">AI assistant connector (MCP)</p>
        <p className="mt-3 max-w-2xl text-[12px] leading-relaxed text-slate-500">
          Add this as a connector in Claude or any MCP client to ask questions about your ledger in
          plain language and raise fee notes by asking. A read-only key exposes 17 tools (listing,
          search and single-record lookups); a read &amp; write key exposes 31, adding create,
          amend and delete for clients, firms, contacts and matters, plus fee notes and receipts.
        </p>
        <p className="num mt-4 break-all border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
          {BASE}/api/mcp
        </p>
        <ol className="mt-4 max-w-2xl list-decimal space-y-1 pl-5 text-[12px] leading-relaxed text-slate-500">
          <li>In Claude: Settings &rarr; Connectors &rarr; Add custom connector.</li>
          <li>Paste the URL above and press Add &mdash; leave the OAuth fields blank.</li>
          <li>Press Connect. A Chambers&rsquo; Billing sign-in page opens.</li>
          <li>Paste one of your <span className="num">ck_live_…</span> keys there and authorise.</li>
          <li>
            Scripts and self-hosted clients can still send the key directly as an{" "}
            <span className="num">X-API-Key</span> header instead.
          </li>
        </ol>
      </div>
    </section>
  );
};
